import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, Chip,
  Alert, LinearProgress, IconButton, Tooltip, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel,
  Collapse, Divider, Avatar
} from '@mui/material';
import ExpandMoreIcon  from '@mui/icons-material/ExpandMore';
import ExpandLessIcon  from '@mui/icons-material/ExpandLess';
import AddIcon         from '@mui/icons-material/Add';
import BusinessIcon    from '@mui/icons-material/Business';
import StoreIcon       from '@mui/icons-material/Store';
import PublicIcon      from '@mui/icons-material/Public';
import RouterIcon      from '@mui/icons-material/Router';
import PeopleIcon      from '@mui/icons-material/People';
import DeleteIcon      from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { useAuth }     from '../contexts/AuthContext';
import api             from '../services/api';

export default function Clients() {
  const { user }                       = useAuth();
  const [hierarchy,    setHierarchy]   = useState([]);
  const [filtered,     setFiltered]    = useState([]);
  const [loading,      setLoading]     = useState(false);
  const [error,        setError]       = useState('');
  const [success,      setSuccess]     = useState('');
  const [expanded,     setExpanded]    = useState({});
  const [countries,    setCountries]   = useState([]);
  const [clientDialog, setClientDialog] = useState(false);
  const [siteDialog,   setSiteDialog]  = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [deleteDialog,   setDeleteDialog]   = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [clientForm,   setClientForm]  = useState({
    country_id: '', name: '', contact: '', email: '', phone: '', address: ''
  });
  const [siteForm, setSiteForm] = useState({
    site_id: '', name: '', address: ''
  });

  const navigate    = useNavigate();
  const isAdmin     = user?.role === 'ADMIN_REDIA';
  const userCountry = user?.country_id;

  useEffect(() => {
    loadHierarchy();
    loadCountries();
  }, []);

  // Filtrer la hiérarchie selon le pays de l'utilisateur
  useEffect(() => {
    if (!hierarchy.length) return;

    if (isAdmin) {
      // Admin voit tout mais son pays est déplié par défaut
      setFiltered(hierarchy);
      if (userCountry) {
        const country = hierarchy.find(c => c.country_id === userCountry);
        if (country) {
          setExpanded(prev => ({ ...prev, [country.country_id]: true }));
          // Déplier aussi le premier client
          if (country.clients.length === 1) {
            setExpanded(prev => ({
              ...prev,
              [`c${country.clients[0].client_id}`]: true
            }));
          }
        }
      }
    } else {
      // Client/Viewer : uniquement son pays
      const userCountryData = hierarchy.filter(c => c.country_id === userCountry);
      setFiltered(userCountryData);
      // Déplier automatiquement
      userCountryData.forEach(country => {
        setExpanded(prev => ({ ...prev, [country.country_id]: true }));
        country.clients.forEach(client => {
          setExpanded(prev => ({ ...prev, [`c${client.client_id}`]: true }));
        });
      });
    }
  }, [hierarchy, userCountry, isAdmin]);

  const loadHierarchy = () => {
    setLoading(true);
    api.get('/v1/hierarchy')
      .then(r => setHierarchy(r.data.hierarchy || []))
      .catch(() => setError('Impossible de charger la hierarchie'))
      .finally(() => setLoading(false));
  };

  const loadCountries = () => {
    api.get('/v1/countries')
      .then(r => setCountries(r.data.countries || []))
      .catch(() => {});
  };

  const toggleCountry = (countryId) => {
    setExpanded(prev => ({ ...prev, [countryId]: !prev[countryId] }));
  };

  const toggleClient = (clientId) => {
    setExpanded(prev => ({ ...prev, [`c${clientId}`]: !prev[`c${clientId}`] }));
  };

  const createClient = async () => {
    try {
      await api.post('/v1/clients', {
        ...clientForm, country_id: parseInt(clientForm.country_id)
      });
      setSuccess(`Client "${clientForm.name}" cree`);
      setClientDialog(false);
      setClientForm({ country_id: '', name: '', contact: '', email: '', phone: '', address: '' });
      loadHierarchy();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur creation client');
    }
  };

  const createSite = async () => {
    if (!selectedClient) return;
    try {
      await api.post(`/v1/clients/${selectedClient.client_id}/sites`, siteForm);
      setSuccess(`Site "${siteForm.name}" cree`);
      setSiteDialog(false);
      setSiteForm({ site_id: '', name: '', address: '' });
      setSelectedClient(null);
      loadHierarchy();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur creation site');
    }
  };

  // Stats sur les données filtrées
  const deleteClient = async () => {
    if (!clientToDelete) return;
    try {
      await api.delete(`/v1/clients/${clientToDelete.client_id}`);
      setSuccess(`Client "${clientToDelete.name}" supprime`);
      setDeleteDialog(false);
      setClientToDelete(null);
      loadHierarchy();
    } catch(e) {
      setError(e.response?.data?.detail || 'Erreur suppression client');
    }
  };

  const totalClients  = filtered.reduce((a, c) => a + c.clients.length, 0);
  const totalSites    = filtered.reduce((a, c) =>
    a + c.clients.reduce((b, cl) => b + cl.sites.length, 0), 0);
  const totalSensors  = filtered.reduce((a, c) =>
    a + c.clients.reduce((b, cl) =>
      b + cl.sites.reduce((d, s) => d + (s.sensor_count || 0), 0), 0), 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1F4E79">
            Clients & Boutiques
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isAdmin
              ? 'Vue administrateur — tous les pays'
              : `Vue filtree — ${filtered[0]?.name || 'votre pays'}`}
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setClientDialog(true)}
            sx={{ bgcolor: '#1F4E79', '&:hover': { bgcolor: '#163a5f' } }}>
            Nouveau client
          </Button>
        )}
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Clients',   value: totalClients,  icon: <BusinessIcon />, color: '#1F4E79' },
          { label: 'Boutiques', value: totalSites,    icon: <StoreIcon />,    color: '#2E75B6' },
          { label: 'Capteurs',  value: totalSensors,  icon: <RouterIcon />,   color: '#4CAF50' },
        ].map(stat => (
          <Grid item xs={4} key={stat.label}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: `${stat.color}15` }}>
                  {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 24 } })}
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700} color={stat.color}>
                    {stat.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Hiérarchie filtrée */}
      {filtered.map(country => (
        <Card key={country.country_id}
          sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 2 }}>
          <CardContent sx={{ p: 0 }}>

            {/* Ligne pays — cliquable seulement pour admin */}
            <Box sx={{
              display: 'flex', alignItems: 'center', p: 2,
              bgcolor: '#1F4E79', borderRadius: '12px 12px 0 0',
              cursor: isAdmin ? 'pointer' : 'default',
              '&:hover': isAdmin ? { bgcolor: '#163a5f' } : {}
            }} onClick={() => isAdmin && toggleCountry(country.country_id)}>
              <PublicIcon sx={{ color: 'white', mr: 1.5 }} />
              <Typography fontWeight={700} color="white" sx={{ flexGrow: 1 }}>
                {country.name}
              </Typography>
              <Chip label={`${country.clients.length} client(s)`} size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mr: 1 }} />
              {isAdmin && (
                expanded[country.country_id]
                  ? <ExpandLessIcon sx={{ color: 'white' }} />
                  : <ExpandMoreIcon sx={{ color: 'white' }} />
              )}
            </Box>

            <Collapse in={isAdmin ? expanded[country.country_id] !== false : true}>
              {country.clients.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary" variant="body2">
                    Aucun client dans ce pays
                  </Typography>
                </Box>
              ) : (
                country.clients.map((client, ci) => (
                  <Box key={client.client_id}>
                    {/* Ligne client */}
                    <Box sx={{ display: 'flex', alignItems: 'center',
                      px: 3, py: 1.5, bgcolor: '#F8FBFF',
                      cursor: 'pointer', '&:hover': { bgcolor: '#EEF4FF' } }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: '#2E75B620',
                        color: '#2E75B6', fontSize: '0.8rem', mr: 1.5 }}>
                        {client.name[0]}
                      </Avatar>
                      <Box sx={{ flexGrow: 1 }}
                        onClick={() => toggleClient(client.client_id)}>
                        <Typography fontWeight={600} fontSize="0.95rem">
                          {client.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {client.email}
                        </Typography>
                      </Box>
                      <Chip label={`${client.sites.length} site(s)`} size="small"
                        sx={{ bgcolor: '#E3F2FD', color: '#1F4E79', mr: 1 }} />
                      {isAdmin && (
                        <Tooltip title="Ajouter un site">
                          <IconButton size="small" color="primary"
                            onClick={() => { setSelectedClient(client); setSiteDialog(true); }}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {isAdmin && (
                        <Tooltip title="Supprimer le client">
                          <IconButton size="small" color="error"
                            onClick={e => { e.stopPropagation(); setClientToDelete(client); setDeleteDialog(true); }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <IconButton size="small"
                        onClick={() => toggleClient(client.client_id)}>
                        {expanded[`c${client.client_id}`] !== false
                          ? <ExpandLessIcon fontSize="small" />
                          : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                    </Box>

                    {/* Sites */}
                    <Collapse in={expanded[`c${client.client_id}`] !== false}>
                      {client.sites.length === 0 ? (
                        <Box sx={{ px: 6, py: 2 }}>
                          <Typography color="text.secondary" variant="body2">
                            Aucun site pour ce client
                          </Typography>
                        </Box>
                      ) : (
                        client.sites.map(site => (
                          <Box key={site.site_id}
                            sx={{ display: 'flex', alignItems: 'center',
                              px: 6, py: 1.5, cursor: 'pointer',
                              '&:hover': { bgcolor: '#F5F5F5' },
                              borderTop: '1px solid #F0F0F0' }}
                            onClick={() => navigate('/dashboard',
                              { state: { siteId: site.site_id } })}>
                            <StoreIcon sx={{ color: '#4CAF50', fontSize: 20, mr: 1.5 }} />
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography fontWeight={600} fontSize="0.9rem">
                                {site.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {site.site_id}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap',
                              justifyContent: 'flex-end' }}>
                              <Chip
                                icon={<RouterIcon sx={{ fontSize: '14px !important' }} />}
                                label={`${site.sensor_count} capteurs`}
                                size="small"
                                sx={{ bgcolor: '#E8F5E9', color: '#4CAF50',
                                  fontSize: '0.7rem' }}
                              />
                              <Chip
                                icon={<PeopleIcon sx={{ fontSize: '14px !important' }} />}
                                label={`${site.visitor_count} visiteurs`}
                                size="small"
                                sx={{ bgcolor: '#E3F2FD', color: '#1F4E79',
                                  fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        ))
                      )}
                    </Collapse>
                    {ci < country.clients.length - 1 && <Divider />}
                  </Box>
                ))
              )}
            </Collapse>
          </CardContent>
        </Card>
      ))}

      {/* Dialog nouveau client (admin only) */}
      <Dialog open={clientDialog} onClose={() => setClientDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau client</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Pays</InputLabel>
                <Select value={clientForm.country_id} label="Pays"
                  onChange={e => setClientForm(p => ({...p, country_id: e.target.value}))}>
                  {countries.map(c => (
                    <MenuItem key={c.country_id} value={c.country_id}>
                      {c.name} ({c.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {[
              ['name',    'Nom du client', true,  12],
              ['contact', 'Contact',       false,  6],
              ['email',   'Email',         false,  6],
              ['phone',   'Telephone',     false,  6],
              ['address', 'Adresse',       false, 12],
            ].map(([key, label, required, xs]) => (
              <Grid item xs={xs} key={key}>
                <TextField fullWidth label={label} required={required}
                  value={clientForm[key]}
                  onChange={e => setClientForm(p => ({...p, [key]: e.target.value}))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setClientDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={createClient}
            disabled={!clientForm.country_id || !clientForm.name}
            sx={{ bgcolor: '#1F4E79' }}>
            Creer le client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog nouveau site */}
      <Dialog open={siteDialog} onClose={() => setSiteDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau site — {selectedClient?.name}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            {[
              ['site_id', 'Site ID (ex: ZARA-CASA-01)', true],
              ['name',    'Nom de la boutique',          true],
              ['address', 'Adresse',                    false],
            ].map(([key, label, required]) => (
              <Grid item xs={12} key={key}>
                <TextField fullWidth label={label} required={required}
                  value={siteForm[key]}
                  onChange={e => setSiteForm(p => ({...p, [key]: e.target.value}))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setSiteDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={createSite}
            disabled={!siteForm.site_id || !siteForm.name}
            sx={{ bgcolor: '#1F4E79' }}>
            Creer le site
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmation suppression client */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: '#d32f2f' }}>Supprimer le client</DialogTitle>
        <DialogContent>
          <Typography>
            Supprimer <strong>{clientToDelete?.name}</strong> ?
            Cette action est irreversible et desactivera le client et tous ses sites.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog(false)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={deleteClient}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
